/* ───────────────────────────────────────────────────
   Reseller Modal — standalone Web Component
   ─────────────────────────────────────────────────── */

import { Swiper } from "swiper";
import type { SwiperOptions } from "swiper/types";
import { Navigation } from "swiper/modules";

interface ResellerStoreData {
  name: string;
  address: string;
  hours: string;
  timezone: string;
  lat: string;
  lng: string;
  images: string[];
}

/* ── Hours helpers (ported from jc-map.js) ── */

const DAY_MAP: Record<string, number> = {
  lundi: 1,
  mardi: 2,
  mercredi: 3,
  jeudi: 4,
  vendredi: 5,
  samedi: 6,
  dimanche: 0,
};

function getDateInTimezone(tz: string) {
  try {
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: tz,
      weekday: "short",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    }).formatToParts(new Date());

    const WDAY: Record<string, number> = {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    };
    let hour = 0,
      minute = 0,
      day = new Date().getDay();

    for (const p of parts) {
      if (p.type === "hour") hour = parseInt(p.value, 10) % 24;
      if (p.type === "minute") minute = parseInt(p.value, 10);
      if (p.type === "weekday" && WDAY[p.value] !== undefined)
        day = WDAY[p.value];
    }
    return { day, minutes: hour * 60 + minute };
  } catch {
    const now = new Date();
    return { day: now.getDay(), minutes: now.getHours() * 60 + now.getMinutes() };
  }
}

function parseDayIndex(name: string): number | undefined {
  return DAY_MAP[name.trim().toLowerCase()];
}

function expandDayRange(text: string): number[] {
  text = text.trim().toLowerCase();
  const days: number[] = [];

  const rangeMatch = text.match(/^(\w+)\s+au\s+(\w+)$/);
  if (rangeMatch) {
    const start = parseDayIndex(rangeMatch[1]);
    const end = parseDayIndex(rangeMatch[2]);
    if (start !== undefined && end !== undefined) {
      let d = start;
      while (true) {
        days.push(d);
        if (d === end) break;
        d = (d + 1) % 7;
      }
    }
    return days;
  }

  const parts = text.split(/\s+et\s+/);
  for (const part of parts) {
    const idx = parseDayIndex(part);
    if (idx !== undefined) days.push(idx);
  }
  if (days.length) return days;

  const single = parseDayIndex(text);
  if (single !== undefined) return [single];

  return [];
}

function parseTimeSlots(timeStr: string) {
  const slots: { openH: number; openM: number; closeH: number; closeM: number }[] = [];
  const parts = timeStr.split(";");
  for (const part of parts) {
    const match = part.match(/(\d{1,2})[Hh](\d{2})\s*-\s*(\d{1,2})[Hh](\d{2})/);
    if (match) {
      slots.push({
        openH: parseInt(match[1], 10),
        openM: parseInt(match[2], 10),
        closeH: parseInt(match[3], 10),
        closeM: parseInt(match[4], 10),
      });
    }
  }
  return slots;
}

function getOpenStatus(hoursText: string, timezone: string): boolean | null {
  if (!hoursText) return null;
  const localTime = getDateInTimezone(timezone || "Europe/Paris");
  const currentDay = localTime.day;
  const currentMinutes = localTime.minutes;

  const entries = hoursText.split(/<br\s*\/?>/i);
  for (const raw of entries) {
    const entry = raw.trim();
    if (!entry) continue;

    const colonIdx = entry.indexOf(":");
    if (colonIdx === -1) continue;
    const dayPart = entry.substring(0, colonIdx).trim();
    const timePart = entry.substring(colonIdx + 1).trim();

    const days = expandDayRange(dayPart);
    if (days.indexOf(currentDay) === -1) continue;

    const slots = parseTimeSlots(timePart);
    for (const slot of slots) {
      const open = slot.openH * 60 + slot.openM;
      const close = slot.closeH * 60 + slot.closeM;
      if (currentMinutes >= open && currentMinutes < close) return true;
    }
  }
  return false;
}

function parseHoursRows(hoursText: string): { days: string; times: string }[] {
  if (!hoursText) return [];
  const rows: { days: string; times: string }[] = [];
  const entries = hoursText.split(/<br\s*\/?>/i);
  for (const raw of entries) {
    const entry = raw.trim();
    if (!entry) continue;
    const colonIdx = entry.indexOf(":");
    if (colonIdx === -1) continue;
    rows.push({
      days: entry.substring(0, colonIdx).trim(),
      times: entry.substring(colonIdx + 1).trim(),
    });
  }
  return rows;
}

/* ── Web Component ── */

export default class ResellerModal extends HTMLElement {
  private boundKeydown: ((e: KeyboardEvent) => void) | null = null;
  private swiper: Swiper | null = null;

  open(data: ResellerStoreData) {
    const images = data.images.filter(Boolean);
    this.innerHTML = this.buildHTML(data, images);
    this.classList.add("is-open");
    document.body.style.overflow = "hidden";

    // Bind close events
    this.querySelector<HTMLElement>(".reseller-modal__close")?.addEventListener(
      "click",
      () => this.close(),
    );
    this.querySelector<HTMLElement>(".reseller-modal__backdrop")?.addEventListener(
      "click",
      () => this.close(),
    );

    // Init Swiper carousel
    if (images.length > 0) {
      const swiperEl = this.querySelector<HTMLElement>(".reseller-modal__carousel");
      if (swiperEl) {
        const swiperParams: SwiperOptions = {
          modules: [Navigation],
          slidesPerView: "auto",
          spaceBetween: 16,
          slidesOffsetBefore: 30,
          slidesOffsetAfter: 30,
        };

        if (images.length > 1) {
          swiperParams.navigation = {
            nextEl: this.querySelector<HTMLElement>(".reseller-modal__carousel-arrow--next")!,
            prevEl: this.querySelector<HTMLElement>(".reseller-modal__carousel-arrow--prev")!,
          };
        }

        this.swiper = new Swiper(swiperEl, swiperParams);

        // Hide prev arrow at first slide
        const prevArrow = this.querySelector<HTMLElement>(".reseller-modal__carousel-arrow--prev");
        if (prevArrow) {
          prevArrow.style.display = "none";
          this.swiper.on("slideChange", (sw) => {
            prevArrow.style.display = sw.activeIndex === 0 ? "none" : "";
          });
        }
      }
    }

    this.boundKeydown = (e: KeyboardEvent) => {
      if (e.key === "Escape") this.close();
    };
    document.addEventListener("keydown", this.boundKeydown);
  }

  close() {
    this.classList.remove("is-open");
    document.body.style.overflow = "";
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    if (this.boundKeydown) {
      document.removeEventListener("keydown", this.boundKeydown);
      this.boundKeydown = null;
    }
    setTimeout(() => {
      if (!this.classList.contains("is-open")) this.innerHTML = "";
    }, 300);
  }

  private buildHTML(data: ResellerStoreData, images: string[]): string {
    const openStatus = getOpenStatus(data.hours, data.timezone);
    const hoursRows = parseHoursRows(data.hours);

    const directionsUrl =
      data.lat && data.lng
        ? `https://www.google.com/maps/dir/?api=1&destination=${data.lat},${data.lng}`
        : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(data.address)}`;

    // Carousel — Swiper
    let carouselHTML = "";
    if (images.length > 0) {
      const slides = images
        .map(
          (src) =>
            `<div class="swiper-slide reseller-modal__slide"><img src="${src}" alt="" loading="lazy" /></div>`,
        )
        .join("");

      const arrows =
        images.length > 1
          ? `<button class="reseller-modal__carousel-arrow reseller-modal__carousel-arrow--prev" aria-label="Image précédente">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <button class="reseller-modal__carousel-arrow reseller-modal__carousel-arrow--next" aria-label="Image suivante">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>`
          : "";

      carouselHTML = `
        <div class="reseller-modal__carousel swiper">
          <div class="swiper-wrapper">${slides}</div>
          ${arrows}
        </div>`;
    }

    // Status
    let statusHTML = "";
    if (openStatus === true) {
      statusHTML = `<p class="reseller-modal__status reseller-modal__status--open"><span class="reseller-modal__status-dot"></span>Ouvert actuellement</p>`;
    } else if (openStatus === false) {
      statusHTML = `<p class="reseller-modal__status reseller-modal__status--closed"><span class="reseller-modal__status-dot"></span>Ferm\u00e9 actuellement</p>`;
    }

    // Hours table
    let hoursHTML = "";
    if (hoursRows.length > 0) {
      const rows = hoursRows
        .map(
          (r) =>
            `<div class="reseller-modal__hours-row"><span class="reseller-modal__hours-day">${r.days}</span><span class="reseller-modal__hours-time">${r.times}</span></div>`,
        )
        .join("");
      hoursHTML = `<div class="reseller-modal__hours">${statusHTML}${rows}</div>`;
    } else if (statusHTML) {
      hoursHTML = `<div class="reseller-modal__hours">${statusHTML}</div>`;
    }

    // Format address: replace commas with line breaks
    const formattedAddress = data.address
      ? data.address.replace(/,\s*/g, ",<br>")
      : "";

    return `
      <div class="reseller-modal__backdrop"></div>
      <div class="reseller-modal__panel" role="dialog" aria-modal="true" aria-label="${data.name}">
        <button class="reseller-modal__close" aria-label="Fermer">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M16 4L4 16" stroke="black" stroke-linejoin="round"/>
<path d="M4 4L16 16" stroke="black" stroke-linejoin="round"/>
</svg>

        </button>
        ${carouselHTML}
        <div class="reseller-modal__content">
          <h3 class="reseller-modal__name">${data.name}</h3>
          ${formattedAddress ? `<p class="reseller-modal__address">${formattedAddress}</p>` : ""}
          ${hoursHTML}
        </div>
        <div class="reseller-modal__cta-wrapper">
          <a href="${directionsUrl}" target="_blank" rel="noopener noreferrer" class="reseller-modal__cta">
            SE RENDRE EN BOUTIQUE
          </a>
        </div>
      </div>`;
  }
}
